/**
 * Landlord Slide-over Alpine.js Component
 *
 * Manages the landlord detail slide-over panel state and data fetching.
 * Supports inline editing for all landlord fields via modals.
 */

import consumer from "../channels/consumer"
import { notesMixin, destroyNote } from './notes_mixin'
import { accountTabMixin } from './account_tab_mixin'
import { legacyImportMixin } from './legacy_import_mixin'
import { transactionSearchMixin } from './transaction_search_mixin'
import { statementModalMixin } from './statement_modal_mixin'
import { landlordInvoiceMixin } from './landlord_invoice_mixin'

export function landlordSlideover() {
  return {
    ...notesMixin,
    ...accountTabMixin('landlord'),
    ...legacyImportMixin('landlord'),
    ...transactionSearchMixin('landlord'),
    ...statementModalMixin(),
    ...landlordInvoiceMixin(),

    // State
    open: false,
    loading: false,
    error: null,
    landlord: null,
    landlordId: null,
    subscription: null,
    zIndex: 100010,

    // Tab state
    activeTab: 'overview',

    // Accordion state (multiple sections can be open)
    openSections: {},

    // Modal states
    identityModalOpen: false,
    identitySaving: false,
    identityError: null,
    identityForm: {},

    contactModalOpen: false,
    contactSaving: false,
    contactError: null,
    contactForm: {},

    financialModalOpen: false,
    financialSaving: false,
    financialError: null,
    financialPasswordRequired: false,
    financialForm: {},

    settingsModalOpen: false,
    settingsSaving: false,
    settingsError: null,
    settingsForm: {},

    savingGscReminder: false,
    savingEicrReminder: false,
    savingMtdStatements: false,

    propertiesModalOpen: false,
    propertiesSaving: false,
    propertiesError: null,
    propertySearchQuery: '',
    propertySearchResults: [],
    propertySearching: false,
    selectedOwnership: null,
    ownershipForm: {},

    // Recurring charges state
    recurringCharges: [],
    recurringLoading: false,
    recurringError: null,
    recurringLoaded: false,

    // Notes editing state (legacy general_notes)
    notesEditing: false,
    notesSaving: false,
    notesError: null,
    notesForm: { general_notes: '' },

    // Documents state
    documentsForm: {
      dragging: false,
      documentsToDelete: [],
      landlordDocumentsToDelete: [],
      saving: false,
      uploadProgress: null
    },

    // Agency Agreement modal state
    agencyAgreementModalOpen: false,
    agencyAgreementUploading: false,
    resendingAgencyAgreement: false,

    // Timeline note adding state
    noteText: '',
    noteSaving: false,
    noteError: null,

    // AI panel state (for backdrop suppression)
    aiPanelOpen: false,

    // NRL/Management Fee/Deposit Scheme editing
    editingNrlStatus: null,
    editingManagementFee: null,
    editingDepositScheme: null,

    // Initialize component
    init() {
      const self = this

      // Subscribe to ActionCable for real-time updates
      this.subscription = consumer.subscriptions.create(
        { channel: "DataUpdatesChannel" },
        {
          received(message) {
            if (message.type === 'landlord' && self.landlord && message.id === self.landlord.id) {
              console.debug('[LandlordSlideover] Received update for landlord', message.id)
              self.handleLandlordUpdate(message)
            }
          }
        }
      )

      // Track AI panel state for backdrop suppression
      window.addEventListener('ai-panel-toggled', (e) => {
        this.aiPanelOpen = e.detail.open
      })

      // Watch for open state changes
      this.$watch('open', (value) => {
        if (value && this.landlordId && !this.landlord) {
          this.fetchLandlord()
        }
      })

      // Lazy-load account data when Account tab is first selected
      this.$watch('activeTab', (tab) => {
        if (tab === 'account' && !this.acctDataLoaded && this.landlordId) {
          this.fetchAccountData(true)
        }
        if (tab === 'recurring' && !this.recurringLoaded && this.landlordId) {
          this.fetchRecurringCharges()
        }
      })

      // Refresh recurring charges when a new charge is created
      window.addEventListener('rent-guarantee-saved', () => {
        if (this.recurringLoaded) this.fetchRecurringCharges()
      })
      window.addEventListener('charge-created', () => {
        if (this.recurringLoaded) this.fetchRecurringCharges()
      })

      // Initialise account tab event listeners
      this.initAccountTab()
    },

    // Open slide-over for a specific landlord
    openForLandlord(landlordId, tab, options = {}) {
      const store = Alpine.store?.('slideovers')
      const { openModal } = options || {}

      // If this landlord is already open, just bring it to front
      if (store?.isOpen?.('landlord', landlordId)) {
        store.bringToFront('landlord', landlordId)
        // Switch tab if requested
        if (tab) this.activeTab = tab
        if (openModal) this._openRequestedModal(openModal)
        return
      }

      // Get next z-index from store for proper stacking
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }

      this.landlordId = landlordId
      this.acctSubTab = 'transactions'
      this.landlord = null
      this.error = null
      this.activeTab = tab || 'overview'
      this.openSections = { properties: true, history: true, financial: true, settings: true, preferences: true }
      this.resetAllModals()
      this.resetAccountState()
      this.recurringCharges = []
      this.recurringLoaded = false
      this.recurringError = null
      this.open = true

      // Register this slideover
      if (store?.register) {
        store.register('landlord', landlordId, this)
      }

      this.fetchLandlord().then(() => {
        if (openModal) this._openRequestedModal(openModal)
      })

      // Explicitly fetch account data if opening on account tab
      // ($watch won't fire if activeTab was already 'account')
      if (this.activeTab === 'account') {
        this.fetchAccountData(true)
      }
    },

    _openRequestedModal(name) {
      if (name === 'agency_agreement' && typeof this.openAgencyAgreementModal === 'function') {
        this.openAgencyAgreementModal()
      }
    },

    // Close slide-over
    close() {
      const store = Alpine.store?.('slideovers')
      if (store?.unregister && this.landlordId) {
        store.unregister('landlord', this.landlordId)
      }

      this.openSections = {}
      this.resetAllModals()
      this.resetAccountState()
      this.clearSearch()

      this.$nextTick(() => {
        this.open = false
      })

      // Close AI panel if it was open in our context
      window.dispatchEvent(new CustomEvent('account-slideover-closed'))
    },

    // Reset all modal states
    resetAllModals() {
      this.identityModalOpen = false
      this.identitySaving = false
      this.identityError = null
      this.identityForm = {}

      this.contactModalOpen = false
      this.contactSaving = false
      this.contactError = null
      this.contactForm = {}

      this.financialModalOpen = false
      this.financialSaving = false
      this.financialError = null
      this.financialForm = {}

      this.settingsModalOpen = false
      this.settingsSaving = false
      this.settingsError = null
      this.settingsForm = {}

      this.savingGscReminder = false
      this.savingEicrReminder = false

      this.propertiesModalOpen = false
      this.propertiesSaving = false
      this.propertiesError = null
      this.propertySearchQuery = ''
      this.propertySearchResults = []
      this.selectedOwnership = null
      this.ownershipForm = {}

      this.notesEditing = false
      this.notesSaving = false
      this.notesError = null
      this.notesForm = { general_notes: '' }

      // Timeline note state
      this.noteText = ''
      this.noteSaving = false
      this.noteError = null

      // Statement modal
      this.statementModalOpen = false
      this.statementGenerating = false

      // Agency Agreement modal
      this.agencyAgreementModalOpen = false
      this.agencyAgreementUploading = false
    },

    // Fetch landlord data from server
    // silent: if true, don't show loading skeleton (for background refreshes)
    async fetchLandlord(silent = false) {
      if (!this.landlordId) return

      if (!silent) {
        this.loading = true
      }
      this.error = null

      try {
        const response = await fetch(`/landlords/${this.landlordId}.json`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        this.landlord = await response.json()
      } catch (error) {
        console.error('Failed to fetch landlord:', error)
        this.error = 'Failed to load landlord details. Please try again.'
      } finally {
        this.loading = false
      }
    },

    // Handle real-time updates from ActionCable
    handleLandlordUpdate(message) {
      if (!this.landlord || message.id !== this.landlord.id) return

      switch (message.action) {
        case 'update':
          Object.assign(this.landlord, message.data)
          break
        case 'destroy':
          this.close()
          break
      }
    },

    // Refresh landlord state after an agency-agreement upload/send in the
    // same window. ActionCable covers other tabs; this is the immediate
    // in-window path so the terms_card flips without waiting on pubsub.
    async refreshAfterAgreementUpdate(landlordId) {
      if (!this.open || !this.landlord || this.landlord.id !== landlordId) return
      await this.fetchLandlord(true)
    },

    // Fetch recurring charges for this landlord's properties
    async fetchRecurringCharges() {
      this.recurringLoading = true
      this.recurringError = null

      try {
        const response = await fetch(`/landlords/${this.landlordId}/recurring_charges.json`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

        this.recurringCharges = await response.json()
        this.recurringLoaded = true
      } catch (error) {
        console.error('Failed to fetch recurring charges:', error)
        this.recurringError = 'Failed to load recurring charges. Please try again.'
      } finally {
        this.recurringLoading = false
      }
    },

    // Save edits to a recurring charge
    async saveRecurringCharge(rc, editForm, cardData) {
      cardData.saving = true
      cardData.editError = null

      const netPence = Math.round(parseFloat(editForm.net_amount_pounds) * 100)
      const vatRate = parseFloat(editForm.vat_rate) || 0
      const vatPence = Math.floor(netPence * vatRate / 100)

      try {
        const response = await fetch(`/accounting/accounts/recurring_charges/${rc.id}.json`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            recurring_charge: {
              description: editForm.description,
              frequency: editForm.frequency,
              net_amount_pence: netPence,
              vat_amount_pence: vatPence,
              start_date: editForm.start_date_editable ? editForm.start_date : undefined,
              end_date: editForm.end_date_editable ? (editForm.end_date || null) : undefined
            }
          })
        })

        if (!response.ok) {
          const data = await response.json()
          throw new Error(data.error || 'Failed to save')
        }

        // Update the local data
        rc.description = editForm.description
        rc.frequency = editForm.frequency
        rc.net_amount_pence = netPence
        rc.vat_amount_pence = vatPence
        if (editForm.start_date_editable) rc.start_date = editForm.start_date
        if (editForm.end_date_editable) rc.end_date = editForm.end_date || null

        cardData.editing = false
      } catch (error) {
        console.error('Failed to save recurring charge:', error)
        cardData.editError = error.message || 'Failed to save. Please try again.'
      } finally {
        cardData.saving = false
      }
    },

    // Cancel a recurring charge (set status to cancelled)
    async cancelRecurringCharge(id) {
      try {
        const response = await fetch(`/accounting/accounts/recurring_charges/${id}.json`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ recurring_charge: { status: 'cancelled' } })
        })

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

        // Remove from the list
        this.recurringCharges = this.recurringCharges.filter(rc => rc.id !== id)
      } catch (error) {
        console.error('Failed to cancel recurring charge:', error)
        alert('Failed to cancel recurring charge. Please try again.')
      }
    },

    // Toggle accordion section
    toggleSection(section) {
      this.openSections[section] = !this.openSections[section]
    },

    isSectionOpen(section) {
      return !!this.openSections[section]
    },

    // ========== Identity Modal ==========
    openIdentityModal() {
      this.identityForm = {
        landlord_type: this.landlord?.landlord_type || 'individual',
        title: this.landlord?.title || '',
        first_name: this.landlord?.first_name || '',
        last_name: this.landlord?.last_name || '',
        display_name: this.landlord?.display_name || '',
        organisation_name: this.landlord?.organisation_name || '',
        salutation: this.landlord?.salutation || '',
        email: this.landlord?.email || '',
        cc_email_1: this.landlord?.cc_email_1 || '',
        cc_email_2: this.landlord?.cc_email_2 || '',
        cc_email_3: this.landlord?.cc_email_3 || '',
        phone: this.landlord?.phone || '',
        phone_2: this.landlord?.phone_2 || '',
        phone_3: this.landlord?.phone_3 || '',
        address_line_1: this.landlord?.address_line_1 || '',
        address_line_2: this.landlord?.address_line_2 || '',
        town: this.landlord?.town || '',
        county: this.landlord?.county || '',
        postcode: this.landlord?.postcode || '',
        country: this.landlord?.country || ''
      }
      this.identityError = null
      this.identityModalOpen = true
    },

    closeIdentityModal() {
      this.identityModalOpen = false
      this.identityError = null
    },

    async saveIdentity() {
      this.identitySaving = true
      this.identityError = null

      try {
        const response = await fetch(`/landlords/${this.landlordId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ landlord: this.identityForm })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.[0] || 'Failed to save')
        }

        this.landlord = await response.json()
        this.closeIdentityModal()

        // Dispatch event for list to update
        window.dispatchEvent(new CustomEvent('landlord-updated', {
          detail: { id: this.landlordId, data: this.landlord }
        }))
      } catch (error) {
        console.error('Failed to save identity:', error)
        this.identityError = error.message || 'Failed to save. Please try again.'
      } finally {
        this.identitySaving = false
      }
    },

    // ========== Contact Modal ==========
    openContactModal() {
      this.contactForm = {
        email: this.landlord?.email || '',
        cc_email_1: this.landlord?.cc_email_1 || '',
        cc_email_2: this.landlord?.cc_email_2 || '',
        cc_email_3: this.landlord?.cc_email_3 || '',
        phone: this.landlord?.phone || '',
        phone_2: this.landlord?.phone_2 || '',
        phone_3: this.landlord?.phone_3 || '',
        address_line_1: this.landlord?.address_line_1 || '',
        address_line_2: this.landlord?.address_line_2 || '',
        town: this.landlord?.town || '',
        county: this.landlord?.county || '',
        postcode: this.landlord?.postcode || '',
        country: this.landlord?.country || ''
      }
      this.contactError = null
      this.contactModalOpen = true
    },

    closeContactModal() {
      this.contactModalOpen = false
      this.contactError = null
    },

    async saveContact() {
      this.contactSaving = true
      this.contactError = null

      try {
        const response = await fetch(`/landlords/${this.landlordId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ landlord: this.contactForm })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.[0] || 'Failed to save')
        }

        this.landlord = await response.json()
        this.closeContactModal()

        window.dispatchEvent(new CustomEvent('landlord-updated', {
          detail: { id: this.landlordId, data: this.landlord }
        }))
      } catch (error) {
        console.error('Failed to save contact:', error)
        this.contactError = error.message || 'Failed to save. Please try again.'
      } finally {
        this.contactSaving = false
      }
    },

    // ========== Financial Modal ==========
    openFinancialModal() {
      // Deep clone NRL statuses to avoid mutating the original
      const nrlStatuses = (this.landlord?.nrl_statuses || []).map(status => ({
        id: status.id,
        apply_from: status.apply_from || '',
        apply_to: status.apply_to || '',
        nrl_applies: status.nrl_applies,
        approval_number: status.approval_number || ''
      }))

      this.financialForm = {
        bank_name: this.landlord?.bank_name || '',
        account_name: this.landlord?.account_name || '',
        account_number: this.landlord?.account_number || '',
        sort_code: this.landlord?.sort_code || '',
        iban_code: this.landlord?.iban_code || '',
        nationality: this.landlord?.nationality || '',
        tax_residency: this.landlord?.tax_residency || '',
        current_password: '',
        nrl_statuses: nrlStatuses
      }
      this.financialError = null
      this.financialModalOpen = true
    },

    closeFinancialModal() {
      this.financialModalOpen = false
      this.financialError = null
      this.financialPasswordRequired = false
    },

    addNrlStatus() {
      this.financialForm.nrl_statuses = this.financialForm.nrl_statuses || []
      this.financialForm.nrl_statuses.push({
        _tempId: Date.now(),
        apply_from: '',
        apply_to: '',
        nrl_applies: false,
        approval_number: ''
      })
    },

    removeNrlStatus(index) {
      const status = this.financialForm.nrl_statuses[index]
      if (status.id) {
        // Mark for destruction
        status._destroy = true
      } else {
        // Remove from array if it's a new record
        this.financialForm.nrl_statuses.splice(index, 1)
      }
    },

    async saveFinancial() {
      this.financialSaving = true
      this.financialError = null

      try {
        // Prepare NRL statuses for nested attributes
        const nrlStatusesAttributes = (this.financialForm.nrl_statuses || []).map(status => {
          const attrs = {
            apply_from: status.apply_from || null,
            apply_to: status.apply_to || null,
            nrl_applies: status.nrl_applies,
            approval_number: status.approval_number || null
          }
          if (status.id) attrs.id = status.id
          if (status._destroy) attrs._destroy = true
          return attrs
        }).filter(s => s.apply_from || s.id) // Only include if has date or is existing

        const payload = {
          current_password: this.financialForm.current_password,
          landlord: {
            bank_name: this.financialForm.bank_name,
            account_name: this.financialForm.account_name,
            account_number: this.financialForm.account_number,
            sort_code: this.financialForm.sort_code,
            iban_code: this.financialForm.iban_code,
            nationality: this.financialForm.nationality,
            tax_residency: this.financialForm.tax_residency,
            nrl_statuses_attributes: nrlStatusesAttributes
          }
        }

        const response = await fetch(`/landlords/${this.landlordId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify(payload)
        })

        if (!response.ok) {
          const errorData = await response.json()
          if (errorData.password_required) {
            this.financialPasswordRequired = true
          }
          throw new Error(errorData.error || errorData.errors?.[0] || 'Failed to save')
        }

        this.landlord = await response.json()
        this.financialForm.current_password = ''
        this.financialPasswordRequired = false
        this.closeFinancialModal()

        window.dispatchEvent(new CustomEvent('landlord-updated', {
          detail: { id: this.landlordId, data: this.landlord }
        }))
      } catch (error) {
        console.error('Failed to save financial:', error)
        this.financialError = error.message || 'Failed to save. Please try again.'
      } finally {
        this.financialSaving = false
      }
    },

    // ========== Settings Modal ==========
    openSettingsModal() {
      // Deep clone management fees (defaults only)
      const managementFees = (this.landlord?.management_fees || []).map(fee => ({
        id: fee.id,
        apply_from: fee.apply_from || '',
        apply_to: fee.apply_to || '',
        fee_percentage: fee.fee_percentage
      }))

      // Deep clone property fee overrides
      const propertyFeeOverrides = (this.landlord?.property_fee_overrides || []).map(fee => ({
        id: fee.id,
        property_id: fee.property_id,
        property_name: fee.property_name,
        fee_percentage: fee.fee_percentage,
        apply_from: fee.apply_from || '',
        apply_to: fee.apply_to || ''
      }))

      // Deep clone deposit schemes
      const depositSchemes = (this.landlord?.deposit_schemes || []).map(scheme => ({
        id: scheme.id,
        scheme_name: scheme.scheme_name || '',
        scheme_id: scheme.scheme_id || ''
      }))

      const tenantFindPence = this.landlord?.default_tenant_find_fee_pence
      const agencyTenantFindPence = this.landlord?.agency_default_tenant_find_fee_pence
      const effectiveTenantFindPence = tenantFindPence != null ? tenantFindPence : agencyTenantFindPence
      this.settingsForm = {
        default_service_type: this.landlord?.default_service_type || '',
        default_deposit_type: this.landlord?.default_deposit_type || '',
        default_tenant_find_fee: effectiveTenantFindPence != null ? (effectiveTenantFindPence / 100).toFixed(2) : '',
        agency_default_tenant_find_fee_pence: agencyTenantFindPence,
        management_fees: managementFees,
        property_fee_overrides: propertyFeeOverrides,
        deposit_schemes: depositSchemes
      }
      this.settingsError = null
      this.settingsModalOpen = true
    },

    closeSettingsModal() {
      this.settingsModalOpen = false
      this.settingsError = null
    },

    addManagementFee() {
      this.settingsForm.management_fees = this.settingsForm.management_fees || []
      this.settingsForm.management_fees.push({
        _tempId: Date.now(),
        apply_from: '',
        apply_to: '',
        fee_percentage: ''
      })
    },

    removeManagementFee(index) {
      const fee = this.settingsForm.management_fees[index]
      if (fee.id) {
        fee._destroy = true
      } else {
        this.settingsForm.management_fees.splice(index, 1)
      }
    },

    addPropertyFeeOverride() {
      this.settingsForm.property_fee_overrides = this.settingsForm.property_fee_overrides || []
      this.settingsForm.property_fee_overrides.push({
        _tempId: Date.now(),
        property_id: '',
        property_name: '',
        apply_from: '',
        apply_to: '',
        fee_percentage: ''
      })
    },

    removePropertyFeeOverride(index) {
      const fee = this.settingsForm.property_fee_overrides[index]
      if (fee.id) {
        fee._destroy = true
      } else {
        this.settingsForm.property_fee_overrides.splice(index, 1)
      }
    },

    get availablePropertiesForOverride() {
      const ownedProperties = this.landlord?.property_ownerships || []
      const usedPropertyIds = (this.settingsForm.property_fee_overrides || [])
        .filter(f => !f._destroy && f.property_id)
        .map(f => f.property_id)
      return ownedProperties
        .map(o => ({ id: o.property_id, name: o.property_display_name }))
        .filter(p => !usedPropertyIds.includes(p.id))
    },

    addDepositScheme() {
      this.settingsForm.deposit_schemes = this.settingsForm.deposit_schemes || []
      this.settingsForm.deposit_schemes.push({
        _tempId: Date.now(),
        scheme_name: '',
        scheme_id: ''
      })
    },

    removeDepositScheme(index) {
      const scheme = this.settingsForm.deposit_schemes[index]
      if (scheme.id) {
        scheme._destroy = true
      } else {
        this.settingsForm.deposit_schemes.splice(index, 1)
      }
    },

    async saveSettings() {
      this.settingsSaving = true
      this.settingsError = null

      try {
        // Prepare management fees for nested attributes
        const managementFeesAttributes = (this.settingsForm.management_fees || []).map(fee => {
          const attrs = {
            apply_from: fee.apply_from || null,
            apply_to: fee.apply_to || null,
            fee_percentage: fee.fee_percentage || null
          }
          if (fee.id) attrs.id = fee.id
          if (fee._destroy) attrs._destroy = true
          return attrs
        }).filter(f => f.apply_from || f.id) // Only include if has date or is existing

        // Prepare property fee overrides as management_fees_attributes with property_id
        const propertyOverrideAttributes = (this.settingsForm.property_fee_overrides || []).map(fee => {
          const attrs = {
            property_id: fee.property_id || null,
            fee_percentage: fee.fee_percentage || null,
            apply_from: fee.apply_from || null,
            apply_to: fee.apply_to || null
          }
          if (fee.id) attrs.id = fee.id
          if (fee._destroy) attrs._destroy = true
          return attrs
        }).filter(f => (f.apply_from && f.property_id && f.fee_percentage) || f.id)

        // Merge default fees and property overrides into one array
        const allFeesAttributes = [...managementFeesAttributes, ...propertyOverrideAttributes]

        // Prepare deposit schemes for nested attributes
        const depositSchemesAttributes = (this.settingsForm.deposit_schemes || []).map(scheme => {
          const attrs = {
            scheme_name: scheme.scheme_name || null,
            scheme_id: scheme.scheme_id || null
          }
          if (scheme.id) attrs.id = scheme.id
          if (scheme._destroy) attrs._destroy = true
          return attrs
        }).filter(s => s.scheme_name || s.id) // Only include if has name or is existing

        const tenantFindFee = this.settingsForm.default_tenant_find_fee
        const enteredPence = tenantFindFee === '' || tenantFindFee == null
          ? null
          : Math.round(parseFloat(tenantFindFee) * 100)
        // If the entered value matches the agency default, send null so the
        // landlord stays on the cascade (no override stored).
        const agencyDefaultPence = this.settingsForm.agency_default_tenant_find_fee_pence
        const tenantFindPence = (enteredPence != null && enteredPence === agencyDefaultPence) ? null : enteredPence

        const payload = {
          landlord: {
            default_service_type: this.settingsForm.default_service_type,
            default_deposit_type: this.settingsForm.default_deposit_type,
            default_tenant_find_fee_pence: tenantFindPence,
            management_fees_attributes: allFeesAttributes,
            deposit_schemes_attributes: depositSchemesAttributes
          }
        }

        const response = await fetch(`/landlords/${this.landlordId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify(payload)
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.[0] || 'Failed to save')
        }

        this.landlord = await response.json()
        this.closeSettingsModal()

        window.dispatchEvent(new CustomEvent('landlord-updated', {
          detail: { id: this.landlordId, data: this.landlord }
        }))
      } catch (error) {
        console.error('Failed to save settings:', error)
        this.settingsError = error.message || 'Failed to save. Please try again.'
      } finally {
        this.settingsSaving = false
      }
    },

    // ========== Preferences ==========
    async toggleGscReminder() {
      if (this.savingGscReminder) return

      this.savingGscReminder = true
      const newValue = !this.landlord.send_gsc_reminder

      try {
        const response = await fetch(`/landlords/${this.landlordId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ landlord: { send_gsc_reminder: newValue } })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.[0] || 'Failed to save')
        }

        this.landlord = await response.json()

        window.dispatchEvent(new CustomEvent('landlord-updated', {
          detail: { id: this.landlordId, data: this.landlord }
        }))
      } catch (error) {
        console.error('Failed to toggle GSC reminder:', error)
        // Revert optimistic update would go here if we had one
      } finally {
        this.savingGscReminder = false
      }
    },

    // ========== EICR Preferences ==========
    async toggleEicrReminder() {
      if (this.savingEicrReminder) return

      this.savingEicrReminder = true
      const newValue = !this.landlord.send_eicr_reminder

      try {
        const response = await fetch(`/landlords/${this.landlordId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ landlord: { send_eicr_reminder: newValue } })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.[0] || 'Failed to save')
        }

        this.landlord = await response.json()

        window.dispatchEvent(new CustomEvent('landlord-updated', {
          detail: { id: this.landlordId, data: this.landlord }
        }))
      } catch (error) {
        console.error('Failed to toggle EICR reminder:', error)
      } finally {
        this.savingEicrReminder = false
      }
    },

    // ========== MTD Quarterly Statement Preferences ==========
    async toggleMtdStatements() {
      await this.saveMtdPreference({ mtd_statements_enabled: !this.landlord.mtd_statements_enabled })
    },

    async updateMtdBasis(basis) {
      await this.saveMtdPreference({ mtd_quarter_basis: basis })
    },

    async saveMtdPreference(attributes) {
      if (this.savingMtdStatements) return

      this.savingMtdStatements = true

      try {
        const response = await fetch(`/landlords/${this.landlordId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ landlord: attributes })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.[0] || 'Failed to save')
        }

        this.landlord = await response.json()

        window.dispatchEvent(new CustomEvent('landlord-updated', {
          detail: { id: this.landlordId, data: this.landlord }
        }))
      } catch (error) {
        console.error('Failed to save MTD preference:', error)
      } finally {
        this.savingMtdStatements = false
      }
    },

    // ========== Notes Editing ==========
    startNotesEditing() {
      this.notesForm.general_notes = this.landlord?.general_notes || ''
      this.notesEditing = true
      this.notesError = null
    },

    cancelNotesEditing() {
      this.notesEditing = false
      this.notesError = null
    },

    async saveNotes() {
      this.notesSaving = true
      this.notesError = null

      try {
        const response = await fetch(`/landlords/${this.landlordId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ landlord: { general_notes: this.notesForm.general_notes } })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.[0] || 'Failed to save')
        }

        this.landlord = await response.json()
        this.notesEditing = false
      } catch (error) {
        console.error('Failed to save notes:', error)
        this.notesError = error.message || 'Failed to save. Please try again.'
      } finally {
        this.notesSaving = false
      }
    },

    // ========== Timeline Notes ==========
    async addNote() {
      if (!this.noteText.trim() || this.noteSaving) return

      this.noteSaving = true
      this.noteError = null

      try {
        const newNote = await this.saveNote({ type: 'Landlord', id: this.landlordId }, this.noteText)

        if (this.landlord) {
          if (!this.landlord.activities) {
            this.landlord.activities = []
          }
          this.landlord.activities.unshift(newNote)
        }

        this.noteText = ''
      } catch (error) {
        console.error('Failed to add note:', error)
        this.noteError = error.message || 'Failed to add note. Please try again.'
      } finally {
        this.noteSaving = false
      }
    },
    removeTimelineEntry(entryId) {
      if (this.landlord?.activities) {
        this.landlord.activities = this.landlord.activities.filter(a => a.id !== entryId)
      }
    },

    // Delete a note from the landlord
    async deleteNote(noteId) {
      if (!confirm('Delete this note?')) return
      try {
        await destroyNote(noteId)
        this.removeTimelineEntry(noteId)
      } catch (error) {
        console.error('Failed to delete note:', error)
        alert('Failed to delete note. Please try again.')
      }
    },

    // ========== Document Management ==========
    addDocuments(files) {
      const fileArray = Array.from(files).filter(f =>
        f.type === 'application/pdf' || f.type.startsWith('image/')
      )
      if (fileArray.length === 0) return
      this.uploadDocuments(fileArray)
    },

    async uploadDocuments(files) {
      this.documentsForm.saving = true
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      try {
        for (let i = 0; i < files.length; i++) {
          this.documentsForm.uploadProgress = files.length > 1
            ? `Analysing file ${i + 1} of ${files.length}…`
            : 'Analysing…'
          const formData = new FormData()
          formData.append('file', files[i])

          const response = await fetch(`/landlords/${this.landlordId}/landlord_documents`, {
            method: 'POST',
            headers: { 'X-CSRF-Token': csrfToken },
            body: formData
          })

          if (!response.ok) {
            const data = await response.json().catch(() => ({}))
            console.error(`Upload failed for ${files[i].name}:`, data.error)
          }
        }
        await this.fetchLandlord(true)
        this.pollForDocumentThumbnails()
      } catch (error) {
        console.error('Failed to upload documents:', error)
        alert('Failed to upload documents. Please try again.')
      } finally {
        this.documentsForm.saving = false
        this.documentsForm.uploadProgress = null
      }
    },

    toggleDocumentDeletion(docId) {
      const index = this.documentsForm.documentsToDelete.indexOf(docId)
      if (index > -1) {
        this.documentsForm.documentsToDelete.splice(index, 1)
      } else {
        this.documentsForm.documentsToDelete.push(docId)
      }
    },

    toggleLandlordDocumentDeletion(docId) {
      const index = this.documentsForm.landlordDocumentsToDelete.indexOf(docId)
      if (index > -1) {
        this.documentsForm.landlordDocumentsToDelete.splice(index, 1)
      } else {
        this.documentsForm.landlordDocumentsToDelete.push(docId)
      }
    },

    async saveDocuments() {
      const legacy  = this.documentsForm.documentsToDelete
      const wrapped = this.documentsForm.landlordDocumentsToDelete
      if (legacy.length === 0 && wrapped.length === 0) return

      // Safety net: warn when this batch would wipe out the only Terms
      // document on file for this landlord. Once they're gone, we lose
      // the proof-of-agreement provenance.
      const allTerms = (this.landlord?.landlord_documents || []).filter(d => d.role === 'terms')
      const wrappedIds = new Set(wrapped)
      const termsBeingDeleted = allTerms.filter(d => wrappedIds.has(d.id))
      if (termsBeingDeleted.length > 0 && termsBeingDeleted.length === allTerms.length) {
        const ok = window.confirm(
          'Are you sure you want to delete these terms? This is the only set of terms on file for this landlord.'
        )
        if (!ok) return
      }

      this.documentsForm.saving = true
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      try {
        if (legacy.length > 0) {
          const formData = new FormData()
          for (const id of legacy) formData.append('documents_to_delete[]', id)
          const r = await fetch(`/landlords/${this.landlordId}/update_documents`, {
            method: 'PATCH',
            headers: { 'X-CSRF-Token': csrfToken },
            body: formData
          })
          if (!r.ok) throw new Error('legacy delete failed')
        }

        if (wrapped.length > 0) {
          const requests = wrapped.map(id =>
            fetch(`/landlords/${this.landlordId}/landlord_documents/${id}`, {
              method: 'DELETE',
              headers: { 'X-CSRF-Token': csrfToken }
            })
          )
          const responses = await Promise.all(requests)
          if (responses.some(r => !r.ok)) throw new Error('wrapped delete failed')
        }

        await this.fetchLandlord(true)
        this.documentsForm.documentsToDelete = []
        this.documentsForm.landlordDocumentsToDelete = []
      } catch (error) {
        console.error('Failed to delete documents:', error)
        alert('Failed to delete documents. Please try again.')
      } finally {
        this.documentsForm.saving = false
      }
    },

    pollForDocumentThumbnails(attempts = 0) {
      const maxAttempts = 10
      const pollInterval = 3000

      const isProcessingPdf = doc => !doc.thumbnail_url && doc.content_type === 'application/pdf'
      const hasProcessingDocs = (this.landlord?.documents?.some(isProcessingPdf)) ||
                                (this.landlord?.landlord_documents?.some(isProcessingPdf))

      if (!hasProcessingDocs || attempts >= maxAttempts || !this.open) {
        return
      }

      setTimeout(async () => {
        if (!this.open) return

        await this.fetchLandlord(true)

        const stillProcessing = (this.landlord?.documents?.some(isProcessingPdf)) ||
                                (this.landlord?.landlord_documents?.some(isProcessingPdf))
        if (stillProcessing && attempts < maxAttempts - 1) {
          this.pollForDocumentThumbnails(attempts + 1)
        }
      }, pollInterval)
    },

    // ========== Agency Agreement Modal ==========
    openAgencyAgreementModal() {
      this.agencyAgreementModalOpen = true
    },

    closeAgencyAgreementModal() {
      this.agencyAgreementModalOpen = false
    },

    async uploadSignedAgreement(files) {
      const file = Array.from(files || []).find(f => f.type === 'application/pdf' || f.type.startsWith('image/'))
      if (!file) return

      this.agencyAgreementUploading = true
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      try {
        const formData = new FormData()
        formData.append('file', file)
        const response = await fetch(`/landlords/${this.landlordId}/agency_agreement/upload`, {
          method: 'POST',
          headers: { 'X-CSRF-Token': csrfToken },
          body: formData
        })

        if (!response.ok) {
          const data = await response.json().catch(() => ({}))
          alert(data.error || 'Failed to upload signed copy.')
          return
        }

        await this.fetchLandlord(true)
        this.pollForDocumentThumbnails()
      } catch (error) {
        console.error('Failed to upload signed agreement:', error)
        alert('Failed to upload signed copy. Please try again.')
      } finally {
        this.agencyAgreementUploading = false
      }
    },

    get agencyAgreementDocumentUrl() {
      return this.landlord?.agency_agreement?.uploaded_document_url || null
    },

    // Resend the in-flight e-sign email by fetching the same composer
    // payload (existing signing request, same token, same audit history)
    // and dispatching the in-app composer event.
    async resendAgencyAgreement() {
      const reqId = this.landlord?.agency_agreement?.signing_request_id
      if (!reqId || this.resendingAgencyAgreement) return
      this.resendingAgencyAgreement = true
      try {
        const r = await fetch(`/external_signing_requests/${reqId}/resend_draft`, {
          headers: { 'Accept': 'application/json' }
        })
        if (!r.ok) {
          const data = await r.json().catch(() => ({}))
          alert(data.error || 'Could not prepare the resend. Please try again.')
          return
        }
        const { draft } = await r.json()
        window.dispatchEvent(new CustomEvent('open-email-compose', {
          detail: {
            to:          draft.recipients,
            subject:     draft.subject,
            body_html:   draft.body_html,
            attachments: draft.attachment ? [ draft.attachment ] : [],
            completion:  draft.completion
          }
        }))
      } catch (e) {
        console.error('Failed to resend agency agreement:', e)
        alert('Network error — please try again.')
      } finally {
        this.resendingAgencyAgreement = false
      }
    },

    // Three-state classification for the Overview-tab terms card.
    get termsCardStatus() {
      const a = this.landlord?.agency_agreement
      if (!a) return 'none'
      if (a.status === 'sent') return 'pending'
      return 'signed' // covers :uploaded and :signed
    },

    get agencyAgreementStatusHeadline() {
      const a = this.landlord?.agency_agreement
      if (a) {
        const v = `v${a.version_number}`
        if (a.status === 'signed' || (a.status === 'uploaded' && a.signed_on)) {
          return `Signed ${v}`
        }
        if (a.status === 'uploaded' && !a.signed_on) {
          return `Uploaded ${v} — signature date not detected`
        }
        if (a.status === 'sent') return `${v} sent — awaiting signature`
        return `${a.status} ${v}`
      }
      const hasTerms = this.landlord?.landlord_documents?.some(d => d.role === 'terms')
      if (hasTerms) {
        const signed = this.landlord.landlord_documents.find(d => d.role === 'terms' && d.signed_on)
        return signed ? 'Signed terms uploaded (no agreement record)' : 'Unsigned terms uploaded'
      }
      return 'No terms on file'
    },

    get agencyAgreementStatusDetail() {
      const a = this.landlord?.agency_agreement
      if (a) {
        if (a.signed_on) {
          const who = a.signer_name ? ` by ${a.signer_name}` : ''
          return `Signed${who} on ${this.formatDate(a.signed_on)}.`
        }
        if (a.status === 'sent' && a.sent_at) {
          return `Sent on ${this.formatDate(a.sent_at)}.`
        }
        if (a.status === 'uploaded' && !a.signed_on) {
          return 'You can edit the signature date manually if needed.'
        }
        return null
      }
      const signedTermsDoc = this.landlord?.landlord_documents?.find(d => d.role === 'terms' && d.signed_on)
      if (signedTermsDoc) {
        const who = signedTermsDoc.signer_name ? ` by ${signedTermsDoc.signer_name}` : ''
        return `Signed${who} on ${this.formatDate(signedTermsDoc.signed_on)}.`
      }
      return null
    },

    formatDate(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return iso
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    },

    // ========== Properties Modal ==========
    openPropertiesModal(ownership = null) {
      if (ownership) {
        this.selectedOwnership = ownership
        this.ownershipForm = {
          property_id: ownership.property_id,
          ownership_percentage: ownership.ownership_percentage || 100,
          start_date: ownership.start_date || '',
          end_date: ownership.end_date || '',
          notes: ownership.notes || ''
        }
      } else {
        this.selectedOwnership = null
        this.ownershipForm = {
          property_id: null,
          ownership_percentage: 100,
          start_date: '',
          end_date: '',
          notes: ''
        }
      }
      this.propertySearchQuery = ''
      this.propertySearchResults = []
      this.propertiesError = null
      this.propertiesModalOpen = true
    },

    closePropertiesModal() {
      this.propertiesModalOpen = false
      this.propertiesError = null
      this.selectedOwnership = null
    },

    async searchProperties() {
      if (!this.propertySearchQuery || this.propertySearchQuery.length < 2) {
        this.propertySearchResults = []
        return
      }

      this.propertySearching = true

      try {
        const response = await fetch(`/landlords/search_properties?q=${encodeURIComponent(this.propertySearchQuery)}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) throw new Error('Search failed')

        this.propertySearchResults = await response.json()
      } catch (error) {
        console.error('Property search failed:', error)
        this.propertySearchResults = []
      } finally {
        this.propertySearching = false
      }
    },

    selectProperty(property) {
      this.ownershipForm.property_id = property.id
      this.propertySearchQuery = property.text
      this.propertySearchResults = []
    },

    async savePropertyOwnership() {
      this.propertiesSaving = true
      this.propertiesError = null

      try {
        const ownershipData = {
          property_id: this.ownershipForm.property_id,
          ownership_percentage: this.ownershipForm.ownership_percentage,
          start_date: this.ownershipForm.start_date || null,
          end_date: this.ownershipForm.end_date || null,
          notes: this.ownershipForm.notes || null
        }

        if (this.selectedOwnership) {
          ownershipData.id = this.selectedOwnership.id
        }

        const response = await fetch(`/landlords/${this.landlordId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            landlord: {
              property_ownerships_attributes: [ownershipData]
            }
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.[0] || 'Failed to save')
        }

        this.landlord = await response.json()
        this.closePropertiesModal()

        window.dispatchEvent(new CustomEvent('landlord-updated', {
          detail: { id: this.landlordId, data: this.landlord }
        }))
      } catch (error) {
        console.error('Failed to save property ownership:', error)
        this.propertiesError = error.message || 'Failed to save. Please try again.'
      } finally {
        this.propertiesSaving = false
      }
    },

    async removePropertyOwnership(ownershipId) {
      if (!confirm('Remove this property ownership?')) return

      try {
        const response = await fetch(`/landlords/${this.landlordId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            landlord: {
              property_ownerships_attributes: [{ id: ownershipId, end_date: new Date().toISOString().split('T')[0] }]
            }
          })
        })

        if (!response.ok) throw new Error('Failed to remove ownership')

        this.landlord = await response.json()

        window.dispatchEvent(new CustomEvent('landlord-updated', {
          detail: { id: this.landlordId, data: this.landlord }
        }))
      } catch (error) {
        console.error('Failed to remove ownership:', error)
        alert('Failed to remove property ownership. Please try again.')
      }
    },

    // ========== Kebab Menu ==========
    kebabOpen: false,

    openAccountSlideover() {
      this.activeTab = 'account'
    },

    getInitials(name) {
      if (!name) return '?'
      name = name.replace(/\([^)]*\)|\[[^\]]*\]/g, ' ')
      return name.split(' ').map(n => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
    },

    // ========== Account Stats Helpers ==========
    formatPence(pence) {
      if (pence === null || pence === undefined) return '-'
      const isNegative = pence < 0
      const absPence = Math.abs(pence)
      const pounds = (absPence / 100).toFixed(2)
      const formatted = parseFloat(pounds).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      return (isNegative ? '-' : '') + '\u00A3' + formatted
    },

    get statValueClass() {
      if (!this.landlord) return 'text-2xl'
      const lengths = [
        this.formatPence(this.landlord.balance_held_pence).length,
        this.formatPence(this.landlord.rent_outstanding_pence).length,
        this.formatPence(this.landlord.reserve_pence).length
      ]
      const max = Math.max(...lengths)
      if (max > 9) return 'text-base'
      if (max > 7) return 'text-lg'
      return 'text-2xl'
    },

    get balanceSubtitle() {
      if (!this.landlord) return ''
      if (this.landlord.balance_held_pence < 0) return 'Money is owed'
      if (this.landlord.balance_held_pence === 0) return 'Nothing to pay'
      return 'Held on account'
    },

    get rentOutstandingSubtitle() {
      if (!this.landlord) return ''
      if (this.landlord.rent_outstanding_pence === 0) return 'All received'
      return 'Outstanding'
    },

    get reserveSubtitle() {
      if (!this.landlord) return ''
      return this.landlord.reserve_pence === 0 ? 'No reserve held' : 'Withheld for costs'
    },

    // ========== Computed Helpers ==========
    get typeBadgeColor() {
      return 'blue'
    },

    get typeBadgeClasses() {
      const colors = {
        blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
        green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        gray: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
      }
      return colors[this.typeBadgeColor] || colors.gray
    },

    formatSortCode(code) {
      if (!code) return ''
      // Remove any existing dashes and format as XX-XX-XX
      const digits = code.replace(/-/g, '')
      if (digits.length === 6) {
        return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 6)}`
      }
      return code
    },

    formatAccountNumber(number) {
      if (!number) return ''
      // Show last 4 digits only for security
      if (number.length === 8) {
        return `****${number.slice(-4)}`
      }
      return number
    }
  }
}
